import { reviewLayerFiles, type LayerSetReview } from './layer-naming'

export const TARGET_LONG_EDGE = 512

export const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]

export function looksLikePng(head: Uint8Array | number[]): boolean {
  if (head.length < PNG_SIGNATURE.length) {
    return false
  }

  return PNG_SIGNATURE.every((byte, i) => head[i] === byte)
}

export type PreCheck =
  | { ok: true }
  | { ok: false; code: 'unreadable_image' | 'bad_dimensions'; width?: number; height?: number }

export function checkDimensions(width: number, height: number): PreCheck {
  if (!width || !height) {
    return { ok: false, code: 'unreadable_image' }
  }
  if (Math.max(width, height) < TARGET_LONG_EDGE) {
    return { ok: false, code: 'bad_dimensions', width, height }
  }

  return { ok: true }
}

export const MAX_SLUG = 64

/**
 * A slug from a display name, matching the API's `IsSlug` rule exactly: lower case, letters,
 * digits, underscore or dash, starting with a letter or digit.
 */
export function slugFromDisplayName(name: string): string {
  const slug = name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+/, '')
    .replace(/_+$/, '')
    .slice(0, MAX_SLUG)

  return /^[a-z0-9]/.test(slug) ? slug : ''
}

export function isValidSlug(slug: string): boolean {
  return slug.length > 0 && slug.length <= MAX_SLUG && /^[a-z0-9][a-z0-9_-]*$/.test(slug)
}

/** An upload id scoped to one attempt. Same character set as an object-key segment. */
export function uploadIdFor(slug: string, stamp: number): string {
  const safe = slug.replace(/[^a-z0-9_-]/g, '') || 'option'

  return `up_${safe}_${stamp.toString(36)}`.slice(0, 64)
}

export interface PlannedFile {
  name: string
  role?: string
  problem?: string
}

export interface UploadPlan {
  review: LayerSetReview
  blocking: number
  canUpload: boolean
}

export function planUpload(filenames: string[]): UploadPlan {
  const review = reviewLayerFiles(filenames)
  const blocking =
    review.problems.length +
    review.mixedBases.length +
    review.duplicateRoles.length +
    (review.missingBase ? 1 : 0)

  return {
    review,
    blocking,
    canUpload: filenames.length > 0 && blocking === 0,
  }
}
